import { getDragAndDropAttributes } from '@components/ArtBoard/DragAndDrop/getDragAndDropAttributes'
import {
  DRAGGABLE_TYPE,
  DROPZONE_TYPE,
} from '@components/ArtBoard/DragAndDrop/types'
import type { SymbolItem } from '@octopus-do/octopus-project-core'
import { thunkAddSymbol } from '@store/migrationActions/thunkActions/thunkSymbolActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { AppState } from '@/types'
import { clsJoin } from '@/utils'
import { SymbolsPage } from '../SymbolsPage'
// biome-ignore lint/suspicious/noShadowRestrictedNames: Symbol is a domain component name
import { Symbol } from './components/Symbol'
import { SymbolDropzone } from './components/SymbolDropzone'
import css from './styles.module.css'

export const Symbols: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const symbolLibrary = useSelector(migrationProjectSelector.getProjectSymbols)

  const isSymbolDragged = useSelector<AppState, boolean>(
    ({ artboard }) =>
      artboard.dragAndDrop.dragged.type === DRAGGABLE_TYPE.SYMBOL
  )

  const onAdd = () => {
    sendGtmEvent(
      GtmTriggerName.SYMBOL_ADD_SIDEBAR,
      'Click to create symbol in sidebar'
    )

    dispatch(thunkAddSymbol())
  }

  return (
    <SymbolsPage onAdd={onAdd}>
      {symbolLibrary.map((symbol: SymbolItem, index) => {
        const pseudoIdUp = `${symbol.id}_${index}_0`
        const pseudoIdDown = `${symbol.id}_${index}_1`
        const symbolData = {
          symbolId: symbol.id,
          index,
        }
        const nextIndex = index !== 0 ? index + 1 : 0

        return (
          <div key={symbol.id}>
            <SymbolDropzone
              symbolId={symbol.id}
              id={pseudoIdUp}
              data={symbolData}
            />

            <div className={css.pseudo_dropzone_wrapper}>
              <Symbol
                id={symbol.id}
                index={index}
                overlay={false}
                isLast={index === symbolLibrary.length - 1}
              />

              <div
                className={clsJoin(
                  css.pseudo_dropzone_up,
                  isSymbolDragged && css.pseudo_dropzone_active
                )}
                {...getDragAndDropAttributes(pseudoIdUp, DROPZONE_TYPE.SYMBOL, {
                  ...symbolData,
                  index: nextIndex,
                })}
              />
              <div
                className={clsJoin(
                  css.pseudo_dropzone_down,
                  isSymbolDragged && css.pseudo_dropzone_active
                )}
                {...getDragAndDropAttributes(
                  pseudoIdDown,
                  DROPZONE_TYPE.SYMBOL,
                  { ...symbolData, index: index + 2 }
                )}
              />
            </div>

            <SymbolDropzone
              symbolId={symbol.id}
              id={pseudoIdDown}
              data={symbolData}
            />
          </div>
        )
      })}
    </SymbolsPage>
  )
}
